import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield } from "lucide-react";
import Navbar from "../../components/Navbar";

const sections = [
  {
    id: "information-we-collect",
    title: "1. Information we collect",
    body: [
      "When you sign up, we collect your name, email, phone number, and any optional profile information you choose to share (sport, skill level, achievements, photos).",
      "When you play, we collect content you publish — posts, comments, match results, tournament entries, articles you read or like.",
      "When you visit, we collect basic technical information (device, browser, IP address) to keep the platform secure and improve performance.",
    ],
  },
  {
    id: "how-we-use",
    title: "2. How we use your information",
    body: [
      "To run the platform: log you in, show your feed, deliver messages, surface relevant tournaments.",
      "To keep play fair and safe: detect abuse, moderate content, verify identity when needed.",
      "To improve Sportofolo: aggregate analytics, prioritize features, fix what's broken.",
      "To talk to you: transactional emails about your account, and occasional product updates you can opt out of.",
    ],
  },
  {
    id: "sharing",
    title: "3. When we share information",
    body: [
      "Publicly: anything you post to your profile, posts, or community feed is visible to other users — that's the point of a community.",
      "With service providers: hosting, email delivery, image storage, analytics. All operate under strict data-protection contracts.",
      "With authorities: only when legally required, and only the minimum necessary.",
      "We never sell your personal information.",
    ],
  },
  {
    id: "your-rights",
    title: "4. Your rights and choices",
    body: [
      "Access — see what we hold about you from your profile settings.",
      "Edit — change or correct your information any time.",
      "Delete — request account deletion and we will erase your personal data within 30 days, except where law requires us to retain it.",
      "Export — ask for a copy of your data in a portable format.",
      "Object — tell us to stop processing your data for marketing at any time.",
    ],
  },
  {
    id: "security",
    title: "5. How we protect your data",
    body: [
      "Encryption in transit (HTTPS) and at rest for sensitive fields.",
      "Role-based access controls so only the people who need data can see it.",
      "Routine security reviews, dependency patching, and incident response drills.",
      "No system is perfectly secure. If anything ever goes wrong, we will tell you within 72 hours.",
    ],
  },
  {
    id: "cookies",
    title: "6. Cookies and tracking",
    body: [
      "We use essential cookies to keep you logged in.",
      "We use analytics cookies (anonymized) to understand which features get used.",
      "We do not run third-party advertising trackers.",
      "You can clear cookies any time from your browser; some features may stop working until you log in again.",
    ],
  },
  {
    id: "children",
    title: "7. Children's data",
    body: [
      "Sportofolo is open to players 13 and over.",
      "Users under 18 should have a parent or guardian aware of their account.",
      "We do not knowingly collect data from anyone under 13. If you believe a child has signed up, write to us and we will remove the account.",
    ],
  },
  {
    id: "international",
    title: "8. International transfers",
    body: [
      "Sportofolo is operated from India. Data is primarily stored in Indian data centers.",
      "Some service providers may process limited data outside India, always under contractual data-protection commitments.",
    ],
  },
  {
    id: "changes",
    title: "9. Changes to this policy",
    body: [
      "We will update this page when our practices change.",
      "If the changes are material, we will email registered users at least 14 days before the new policy takes effect.",
      "The date below tells you when this policy was last revised.",
    ],
  },
  {
    id: "contact",
    title: "10. Contact us",
    body: [
      "Questions, requests, or concerns about your data? Write to us at privacy@sportofolo.com or use the Contact page.",
      "We respond to every request within 30 days.",
    ],
  },
];

const PrivacyPolicyPage: React.FC = () => (
  <div className="min-h-screen bg-white text-gray-900">
    <Navbar />
    {/* ===== HERO ===== */}
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.18),_transparent_60%)]" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-16 sm:pt-32 sm:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur px-4 py-1.5 rounded-full mb-5">
            <Shield size={16} className="text-emerald-400" />
            <span className="text-sm font-medium">Your data, your call.</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed max-w-2xl">
            We collect the minimum we need to run a great sports community, we
            keep it secure, and we never sell it. Here's exactly what that
            means.
          </p>
          <p className="text-sm text-gray-500 mt-6">
            Last updated: May 26, 2026
          </p>
        </motion.div>
      </div>
    </section>

    {/* ===== TOC + BODY ===== */}
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
      <div className="grid lg:grid-cols-4 gap-10 lg:gap-14">
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24">
            <p className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-3">
              On this page
            </p>
            <ul className="space-y-2 text-sm">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-gray-600 hover:text-indigo-600 transition block py-1"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="lg:col-span-3 space-y-12">
          {sections.map((s) => (
            <article key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">{s.title}</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                {s.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    {/* ===== CTA ===== */}
    <section className="bg-gradient-to-br from-gray-50 to-white py-16 sm:py-20 border-t border-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold mb-3">
          Questions about your data?
        </h2>
        <p className="text-gray-600 mb-6">
          Write to us — we read every message.
        </p>
        <Link
          to="/contact"
          className="inline-block bg-slate-900 text-white px-8 py-3 rounded-full font-semibold hover:bg-slate-800 transition"
        >
          Contact Us
        </Link>
      </div>
    </section>
  </div>
);

export default PrivacyPolicyPage;
